/**
 * Normalize email for invitation matching (case-insensitive, trimmed).
 * @param {unknown} s
 * @returns {string}
 */
export function normalizeInvitationEmail(s) {
  return String(s ?? '').trim().toLowerCase();
}

/**
 * Whether the signed-in user may accept this invite token's association.
 * If the invite has no email stored, we allow (legacy tokens).
 * If it has an email, the user must be signed in with the same email.
 * @param {unknown} inviteEmail - from inviteTokens doc
 * @param {unknown} userEmail - from Firebase Auth user
 * @returns {boolean}
 */
export function canAcceptInvitationWithCurrentUser(inviteEmail, userEmail) {
  const inv = normalizeInvitationEmail(inviteEmail);
  const usr = normalizeInvitationEmail(userEmail);
  if (!inv) return true;
  if (!usr) return false;
  return inv === usr;
}

/**
 * Map Firestore / app errors from invitation linking to a short code for i18n.
 * @param {unknown} err
 * @returns {'permission' | 'email' | null}
 */
export function invitationLinkErrorKind(err) {
  const code = err?.code || '';
  const msg = String(err?.message || '');
  if (code === 'INVITATION_EMAIL_MISMATCH' || msg.includes('INVITATION_EMAIL_MISMATCH')) return 'email';
  if (
    code === 'permission-denied' ||
    msg.includes('permission-denied') ||
    /missing or insufficient permissions/i.test(msg)
  ) {
    return 'permission';
  }
  return null;
}

/**
 * User-facing message for invitation linking failures (Signup / home redirect).
 * @param {unknown} err
 * @param {(key: string) => string} translate - e.g. LanguageContext `t`
 * @param {unknown} invitedEmail - from invite token doc
 */
export function formatInvitationAcceptError(err, translate, invitedEmail) {
  const label = invitedEmail ? String(invitedEmail).trim() : '';
  const display = label || '—';
  const fill = (key) => String(translate(key)).replace(/\{email\}/g, display);
  const kind = invitationLinkErrorKind(err);
  if (kind === 'email') return fill('auth.invitationWrongAccount');
  if (kind === 'permission') return fill('auth.invitationLinkPermissionHelp');
  const msg = String(err?.message || '');
  if (msg) return msg;
  return fill('auth.linkingBrokerError');
}
